import type { TicketEffortCategory, TicketEffortView } from '@nightshift/ui-types';

import { useTimeFormat } from '../../lib/display.ts';
import { compactDuration, observedTime, usd } from '../../lib/format.ts';
import './effort.css';

const CATEGORY_LABEL: Record<TicketEffortCategory, string> = {
  queue: 'Queued for a runner',
  agent: 'Agent execution',
  ci: 'CI',
  'evidence-transport': 'Evidence transport',
  'human-wait': 'Waiting on a person',
  'automatic-wait': 'Automatic wait',
  landing: 'Landing',
};

/**
 * Where the ticket's time and money went, and what is holding it now.
 *
 * Wall and resource time are printed side by side because they answer different questions:
 * how long a person waited, and how much machine time was spent. Two agents running for an
 * hour is one hour of wall time and two of resource time, and adding them would be the mistake
 * the proposal names. Unknown usage is marked, never counted as free.
 */
export function EffortSummary({ effort, now }: { effort: TicketEffortView; now: number }) {
  const format = useTimeFormat();
  const shown = effort.categories.filter((row) => row.wallMs > 0);
  return (
    <section className="effort" aria-label="Elapsed time and cost">
      <div className="effort-holding">
        <span className="drawer-section-kicker">What is holding this ticket?</span>
        <p className="effort-holding-what">{effort.holding.what}</p>
        <p className="dim effort-holding-since">
          since{' '}
          <time dateTime={new Date(effort.holding.since).toISOString()}>{observedTime(effort.holding.since, now, format)}</time>
          {effort.holding.ref ? (
            <>
              {' · '}
              {effort.holding.ref.kind === 'pipeline' && /^https?:/.test(effort.holding.ref.label) ? (
                <a href={effort.holding.ref.label} target="_blank" rel="noreferrer">pipeline {effort.holding.ref.id}</a>
              ) : (
                <span>{effort.holding.ref.label}</span>
              )}
            </>
          ) : null}
        </p>
      </div>
      <dl className="effort-totals">
        <div>
          <dt>Elapsed</dt>
          <dd className="num">{compactDuration(effort.wallMs)}</dd>
        </div>
        <div>
          <dt>Resource time</dt>
          <dd className="num">{compactDuration(effort.resourceMs)}</dd>
        </div>
        <div>
          <dt>Known cost</dt>
          <dd className="num">
            {usd(effort.cost.knownUsd)}
            {effort.cost.unknownCount > 0 ? (
              <span className="dim"> +{effort.cost.unknownCount} unknown</span>
            ) : null}
          </dd>
        </div>
      </dl>
      {shown.length > 0 ? (
        <table className="effort-table">
          <thead>
            <tr>
              <th>Phase</th>
              <th className="num">Wall</th>
              <th className="num">Resource</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.category} data-category={row.category}>
                <td>{CATEGORY_LABEL[row.category]}</td>
                <td className="num">{compactDuration(row.wallMs)}</td>
                <td className="num">{compactDuration(row.resourceMs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="dim">No time recorded yet.</p>
      )}
      {effort.recurrence ? (
        <p className={`effort-recurrence${effort.recurrence.streak >= effort.recurrence.threshold ? ' is-repeated' : ''}`}>
          {effort.recurrence.streak >= 2 ? `Same failure ×${effort.recurrence.streak}` : 'Latest failure'}: {effort.recurrence.summary}
          {effort.recurrence.strategy !== 'repair' ? ` · strategy: ${effort.recurrence.strategy}` : ''}
        </p>
      ) : null}
      {effort.ci ? (
        <p className="dim effort-ci">
          CI: {effort.ci.pipelines} {effort.ci.pipelines === 1 ? 'pipeline' : 'pipelines'} · queued {compactDuration(effort.ci.queuedMs)} · ran{' '}
          {compactDuration(effort.ci.executionMs)}
          {effort.ci.byRunner.length > 0 ? ` · ${effort.ci.byRunner.map((row) => `${row.runner} ${compactDuration(row.executionMs)}`).join(', ')}` : ''}
        </p>
      ) : null}
      {effort.continuation ? (
        <div className={`effort-continuation${effort.continuation.noProgressStreak >= effort.continuation.threshold ? ' is-stalled' : ''}`}>
          <p className="effort-continuation-total">
            {effort.continuation.attempts === 1
              ? 'One attempt on this implementation'
              : `${effort.continuation.attempts} attempts on this implementation (${effort.continuation.continuations} ${effort.continuation.continuations === 1 ? 'continuation' : 'continuations'})`}
            {effort.continuation.extensions > 0 ? `, extended ${effort.continuation.extensions}×` : ''}
            {' · '}
            {compactDuration(effort.continuation.wallMs)} · {usd(effort.continuation.costUsd)}
            {effort.continuation.unknownCostCount > 0 ? ` +${effort.continuation.unknownCostCount} unknown` : ''}
          </p>
          {effort.continuation.noProgressStreak > 0 ? (
            <p className="effort-continuation-stall">
              No progress on the last {effort.continuation.noProgressStreak === 1 ? 'continuation' : `${effort.continuation.noProgressStreak} continuations`}
              {effort.continuation.noProgressStreak >= effort.continuation.threshold ? ' · strategy changes before the next one' : ''}
            </p>
          ) : null}
          {effort.continuation.checkpoints.length > 0 ? (
            <ul className="effort-checkpoints">
              {effort.continuation.checkpoints.map((row) => (
                <li key={row.attemptId} data-progressed={row.progressed ? 'yes' : 'no'}>
                  <span className="dim">Attempt {row.attemptId}</span> {row.summary}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {effort.repairs.length > 0 ? (
        <p className="dim effort-repairs">
          Repairs:{' '}
          {Object.entries(
            effort.repairs.reduce<Record<string, number>>((acc, row) => {
              acc[row.category] = (acc[row.category] ?? 0) + 1;
              return acc;
            }, {}),
          )
            .map(([category, count]) => `${category} ×${count}`)
            .join(', ')}
        </p>
      ) : null}
      {effort.invocations.length > 0 ? (
        <details className="effort-invocations">
          <summary className="dim">
            {effort.invocations.length} {effort.invocations.length === 1 ? 'invocation' : 'invocations'}
          </summary>
          <ul>
            {effort.invocations.map((row) => (
              <li key={`${row.kind}-${row.id}-${row.harness}-${row.model}`} className={row.interrupted ? 'is-interrupted' : undefined}>
                <span>{row.label}</span>
                <span className="dim">
                  {[row.harness, row.model].filter(Boolean).join(' ')}
                  {row.durationMs !== null ? ` · ${compactDuration(row.durationMs)}` : ''}
                  {row.interrupted ? ' · interrupted' : ''}
                </span>
                <span className="num">{row.costUsd === null ? 'unknown' : usd(row.costUsd)}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
